import { useEffect, useState } from 'react';
import { FRAME_COLOUR_SWATCH, SIZE_LABELS } from '../design/products';
import type { Design } from '../design/schema';
import { posterLayout } from '../poster/layout';
import { renderPreview } from '../services/api';
import { Modal } from './ui';

/**
 * Shows the design as rendered by the print pipeline (the same renderer that makes the
 * print file), mounted in the chosen frame. Tap the print to inspect it at full detail.
 */
export default function PreviewModal({ design, onClose }: { design: Design; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const { product } = design;
  const layout = posterLayout(product);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    renderPreview(design, 2).then(
      (blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      },
      (e) => alive && setError(e.message || 'Preview failed'),
    );
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // The modal previews the design as it was when opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const framed = product.framing !== 'Unframed';
  const frameColour = framed && product.frameColour !== 'N/A' ? FRAME_COLOUR_SWATCH[product.frameColour] : null;
  const size = SIZE_LABELS[product.size];

  return (
    <Modal title="Print preview" onClose={onClose} wide>
      <div className={`tmd-preview${zoomed ? ' is-zoomed' : ''}`}>
        {error ? (
          <p className="tmd-alert">{error}</p>
        ) : !url ? (
          <div className="tmd-preview-wait" style={{ aspectRatio: `${layout.width} / ${layout.height}` }}>
            <span className="tmd-spinner" /> Rendering your print…
          </div>
        ) : zoomed ? (
          <button type="button" className="tmd-preview-detail" onClick={() => setZoomed(false)} title="Back to framed view">
            <img src={url} alt="Print at full detail" />
          </button>
        ) : (
          <button type="button" className="tmd-preview-wall" onClick={() => setZoomed(true)} title="Inspect detail">
            <span
              className={`tmd-frame${framed ? ' is-framed' : ''}${product.framing === 'Framed with white mount' ? ' has-mount' : ''}`}
              style={frameColour ? ({ '--tmd-frame-colour': frameColour } as React.CSSProperties) : undefined}
            >
              <img src={url} alt="Your print" />
            </span>
          </button>
        )}
        <p className="tmd-preview-caption">
          {size.inches} ({size.cm}) · {product.framing}
          {frameColour ? ` · ${product.frameColour}` : ''}
          {url && !error && <> · {zoomed ? 'tap to go back' : 'tap the print to inspect the detail'}</>}
        </p>
      </div>
    </Modal>
  );
}
